import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "ss_lang";

const translations = {
  en: {
    dir: "ltr",
    nav: {
      home: "Home",
      about: "About",
      services: "Services",
      pricing: "Pricing",
      demos: "Demos",
      contact: "Contact",
      whatsapp: "WhatsApp",
      cta: "Start a Project",
    },
    hero: {
      badgeStudio: "Digital Studio",
      headlineLine1: "Smart Digital",
      headlineLine2Gold: "Solutions",
      headlineLine2Rest: "for Local",
      headlineLine3: "Businesses.",
      subheadline:
        "Salama Solutions helps restaurants, cafés, clinics, and local companies grow with modern websites, automation, booking systems, ordering systems, and smart business tools.",
      ctaDemos: "View Demos",
      ctaContact: "Contact Us",
      statProjects: "Projects shipped",
      statNiches: "Core niches",
      statRetention: "Client retention",
      statDelivery: "Avg. delivery",
    },
    services: {
      eyebrow: "What We Do",
      title: "Eight services. One studio.",
      sub: "We design and engineer the digital systems that quietly run modern local businesses, from a single page brochure to a full AI powered ops stack.",
      items: [
        {
          title: "Business Websites",
          short: "Premium, fast, and SEO ready websites built to convert.",
          bullets: [
            "Custom design system",
            "Mobile, tablet, desktop ready",
            "Lightning fast performance",
            "On page SEO baseline",
          ],
        },
        {
          title: "Online Ordering Systems",
          short: "Take orders online without the giant commission fees.",
          bullets: [
            "Menu and modifiers",
            "Pickup or delivery",
            "WhatsApp order routing",
            "Order dashboard",
          ],
        },
        {
          title: "Booking & Appointments",
          short: "Let clients book in seconds, 24/7.",
          bullets: [
            "Smart availability",
            "Auto-confirmations",
            "Reminders and missed appointment alerts",
            "Calendar sync",
          ],
        },
        {
          title: "WhatsApp Automation",
          short: "Automatic reply, route leads, and confirm bookings on WhatsApp.",
          bullets: [
            "Smart automatic reply",
            "Order & booking confirmations",
            "Lead routing",
            "Broadcasts and re engagement",
          ],
        },
        {
          title: "AI Customer Support Agents",
          short: "24/7 AI agents trained on your business.",
          bullets: [
            "Trained on your data",
            "Web and WhatsApp deployable",
            "Human handoff",
            "Conversation analytics",
          ],
        },
        {
          title: "Business Dashboards",
          short: "See your business at a glance, in real time.",
          bullets: [
            "Real time KPIs",
            "Role based access",
            "Exports & reports",
            "Mobile-friendly",
          ],
        },
        {
          title: "Smart Home & Smart Office",
          short: "Voice, sensors, and automations that just work.",
          bullets: [
            "Lighting & climate",
            "Access control",
            "Voice assistants",
            "Custom routines",
          ],
        },
        {
          title: "Custom IT Solutions",
          short: "Tailored systems for tricky business problems.",
          bullets: [
            "Internal tools",
            "Integrations and APIs",
            "Workflow automation",
            "Tech consultation",
          ],
        },
      ],
    },
    servicesPage: {
      eyebrow: "Services",
      heading1: "Studio",
      headingGold: "capabilities,",
      headingRest: "built for local growth.",
      sub: "Eight focused services. We can deliver any one of them as a standalone, or stack them into a full digital operating system for your business.",
      marquee: ["Design", "Engineering", "Automation", "AI", "Strategy"],
      ctaTitle: "Not sure where to start?",
      ctaSub: "Send us your business name and one problem you wish would solve itself. We'll reply with a clean plan.",
      ctaButton: "Talk to us",
    },
    whoWeHelp: {
      eyebrow: "Who We Help",
      title: "Built for the businesses your neighborhood loves.",
      sub: "We focus deep on three niches so the systems we ship are sharper, not generic.",
      liveDemo: "Live demo",
      niches: [
        {
          name: "Restaurants",
          blurb: "Online ordering, reservations, menu management, and WhatsApp automation that fills more tables.",
        },
        {
          name: "Cafés",
          blurb: "Loyalty programs, mobile ordering, and elegant brand sites that turn first timers into regulars.",
        },
        {
          name: "Clinics",
          blurb: "Appointment booking, reminders, patient intake, and clean trustworthy sites that build confidence.",
        },
      ],
    },
    whyUs: {
      eyebrow: "Why Choose SS",
      title: "Premium craft. Real outcomes.",
      sub: "We combine designer-grade taste with serious engineering — and we only ship work we'd put our own name on (because we do).",
      items: [
        {
          title: "Built for local businesses",
          body: "We know small ops. No bloated SaaS, no jargon — just systems that pay for themselves.",
        },
        {
          title: "Premium design, real engineering",
          body: "Designs that look like a brand you'd trust, with code that won't break in two months.",
        },
        {
          title: "AI where it earns its keep",
          body: "We use AI when it saves time or money — not as a sticker on the homepage.",
        },
        {
          title: "Fast delivery, clear pricing",
          body: "Most builds ship in days, not months. You see scope, price, and timeline upfront.",
        },
      ],
    },
    about: {
      eyebrow: "Who's Behind This",
      title: "A studio with a name, not just a logo.",
      sub: "You're not hiring a faceless agency. You're working directly with the person who builds and stands behind every project.",
      founderName: "Ahmad Salama",
      founderRole: "Founder, Salama Solutions",
      basedIn: "Based in Ramallah, Palestine",
      bioParagraphs: [
        "Ahmad Salama is the founder of Salama Solutions, working out of Ramallah. He taught himself how to build software long before he studied it formally, and both halves still shape how he works today: the instinct of someone who learned by building real things, paired with the discipline of someone who went back and studied the fundamentals properly.",
        "He started this studio for a simple reason. Every restaurant owner, café owner, and clinic he talked to felt like technology was something built for someone else, then handed down to them as a template. Ahmad wanted the opposite. He wanted every business owner he works with to open their new site or system and feel like it was actually made for their community, their customers, their name above the door.",
        "That is still how he works. He answers the WhatsApp himself, reads every message that comes through the contact form, and builds every project personally rather than passing it to a team you never meet. If you reach out to Salama Solutions, you are talking directly to the person who will build your project.",
      ],
      studioLine: "Salama Solutions is a Palestinian digital studio building websites, ordering systems, booking tools, and automation for restaurants, cafés, and clinics across the region — work designed and shipped locally, by someone who understands the market it's built for.",
    },
    aboutPage: {
      eyebrow: "About",
      heading1: "Meet the studio",
      headingGold: "behind your project.",
      sub: "No account managers, no hand-offs — just the person who designs, builds, and ships your project, start to finish.",
    },
    demoCards: {
      eyebrow: "Demo Websites",
      title: "Four premium demos. Four unique vibes.",
      sub: "Every demo lives at its own route, with its own brand, palette, and feature set — exactly how we'd ship to a real client.",
      viewDemo: "View live demo",
    },
    demosList: {
      restaurant: { type: "Restaurant", description: "Wood fired Mediterranean kitchen with online ordering and reservations." },
      palo: { type: "Café", description: "Full café system with ordering, loyalty rewards, admin controls, barista tasks, and live analytics." },
      cafe: { type: "Café", description: "Specialty café with loyalty program, ordering, and warm storytelling." },
      clinic: { type: "Clinic", description: "Trustworthy primary care site with online appointment booking." },
    },
    demosPage: {
      eyebrow: "Demo Showcase",
      heading1: "Four premium demos.",
      headingGold: "Four distinct vibes.",
      sub: "Each demo lives at its own route, with its own brand and feature set — exactly how we'd ship to a real client.",
    },
    pricing: {
      eyebrow: "Pricing Plans",
      title: "Clear scope. Honest prices.",
      sub: "Three starting points. Every plan is tailored to your business — what you see is the floor, not a hidden ceiling.",
      startingFrom: "Starting from",
      mostPopular: "Most popular",
      deliveryLabel: "Delivery",
      startWith: (name) => `Start with ${name}`,
      paymentMethods: "Accepted payment methods: Bank transfer & cash",
      plans: {
        basic: {
          name: "Basic",
          tagline: "Get a professional online presence — fast.",
          audience: "For small businesses that need a clean, credible website.",
          delivery: "5–7 days",
          features: [
            "Single page website",
            "Mobile responsive design",
            "Contact / WhatsApp button",
            "Basic SEO setup",
            "Hosting setup guidance",
          ],
        },
        standard: {
          name: "Standard",
          tagline: "A real business site with customer interaction.",
          audience: "For businesses that need menus, services, or booking forms.",
          delivery: "7–14 days",
          features: [
            "Multi-section website",
            "Menu, services or booking form",
            "WhatsApp automation",
            "Basic dashboard or form management",
            "1 revision round",
            "Domain + email setup support",
          ],
        },
        premium: {
          name: "Premium",
          tagline: "A full smart system, end to end.",
          audience: "For businesses that want a real digital edge.",
          delivery: "2–4 weeks",
          features: [
            "Full custom website",
            "Booking or ordering system",
            "AI chatbot or support agent",
            "Admin dashboard demo",
            "Automation workflows",
            "Priority support",
          ],
        },
      },
      note: "Prices can change depending on business needs and system complexity. We send a clear quote before any work starts.",
    },
    pricingPage: {
      eyebrow: "Pricing",
      heading1: "Simple plans.",
      headingGold: "Real systems.",
      sub: "Pick the starting point that fits your business. We'll send a tailored quote with the final scope before any work begins.",
    },
    notFound: {
      badge: "Page not found",
      heading: "Nothing here.",
      body: "The page you tried to open does not exist. Head back home and keep exploring the studio.",
      backHome: "Back home",
      viewDemos: "View demos",
    },
    marquee: [
      "Websites",
      "Online Ordering",
      "Booking Systems",
      "WhatsApp Automation",
      "AI Agents",
      "Dashboards",
      "Smart Spaces",
      "Custom IT",
    ],
    contact: {
      eyebrow: "Contact",
      title: "Tell us what you're building.",
      sub: "Send a quick note. We reply within one business day with scope, price, and timeline — no hard sales.",
      emailLabel: "Email",
      whatsappLabel: "WhatsApp",
      respondNote:
        "We respond Monday – Saturday. For instant questions, WhatsApp is fastest.",
      fieldName: "Your name",
      fieldEmail: "Your email",
      fieldBusiness: "Business type",
      fieldService: "Service needed",
      fieldMessage: "Message",
      placeholderName: "Jane Doe",
      placeholderEmail: "jane@example.com",
      placeholderMessage:
        "Tell us what you're building, what you've tried, and what success looks like.",
      errorName: "Please add your name.",
      errorEmail: "Please add your email.",
      errorMessage: "Add a quick brief, even one line works.",
      sending: "Sending...",
      send: "Send Message",
      chatWhatsapp: "Chat on WhatsApp",
      privacyNote: "We never share your info. Ever.",
      privacyLink: "Privacy Policy",
      successTitle: "Message received.",
      successBody:
        "we'll get back to you within one business day. For instant chat, ping us on WhatsApp.",
      thereFallback: "there",
      sendError:
        "Failed to send message. Please try again or contact us on WhatsApp.",
    },
    contactPage: {
      heading1: "Let's build something",
      headingGold: "people remember.",
    },
    businessTypes: [
      "Restaurant",
      "Café",
      "Clinic",
      "Retail Shop",
      "Salon / Spa",
      "Hotel / Hostel",
      "Real Estate",
      "Other",
    ],
    serviceOptions: [
      "Business Website",
      "Online Ordering System",
      "Booking & Appointments",
      "WhatsApp Automation",
      "AI Customer Support Agent",
      "Business Dashboard",
      "Smart Home / Smart Office",
      "Custom IT Solution",
      "Not sure yet — advise me",
    ],
    footer: {
      studio: "Studio",
      demos: "Demos",
      rights: "All rights reserved.",
      builtNote: "Built in-house · Crafted with care",
      privacyPolicy: "Privacy Policy",
      credibility: "Salama Solutions · Operating since 2024",
      tagline: "Smart digital systems, websites, automation, booking & ordering systems, and AI tools for local businesses.",
    },
    privacyPage: {
      eyebrow: "Legal",
      titleMain: "Privacy",
      titleGold: "Policy",
      lastUpdated: "Last updated: July 28, 2026",
      shortVersion:
        "Short version: your information stays with us, safe and sound. We only use it to talk to you about your project — never sold, never shared, never handed off to anyone else.",
      sections: [
        {
          title: "1. Who we are",
          body: "Salama Solutions is a digital studio building websites, ordering systems, booking tools, and automation for local businesses. This page explains, in plain language, what happens to your information when you reach out to us.",
        },
        {
          title: "2. What we collect",
          body: "When you use our contact form or message us on email/WhatsApp, we collect what you share with us directly: your name, email address, phone/WhatsApp number, business type, and the details of your message. We don't collect payment information through this website.",
        },
        {
          title: "3. How we use it",
          body: "We use your information for one reason: to reply to you, prepare your quote, and deliver the project you hire us for. That's it — we don't sell, rent, or hand it off to anyone else, and we don't use it for marketing you didn't ask for.",
        },
        {
          title: "4. Keeping it safe",
          body: "Your details stay with us, safe and sound. Contact form messages travel through a standard, encrypted email service to reach our inbox, and WhatsApp messages stay inside WhatsApp's own secure platform. We don't run tracking or advertising cookies on this site.",
        },
        {
          title: "5. Your rights",
          body: "You can ask us at any time to tell you what information we hold about you, correct it, or delete it. Contact us using the details below and we'll take care of it promptly.",
        },
        {
          title: "6. Changes to this policy",
          body: "We may update this page occasionally as the business grows. The latest version will always be available here.",
        },
      ],
      contactSectionTitle: "7. Contact us",
      contactSectionBody: (email, whatsapp) =>
        `Questions about this policy or your data? Email ${email} or message us on WhatsApp at ${whatsapp}.`,
    },
  },
  ar: {
    dir: "rtl",
    nav: {
      home: "الرئيسية",
      about: "من نحن",
      services: "خدماتنا",
      pricing: "الأسعار",
      demos: "نماذج",
      contact: "تواصل معنا",
      whatsapp: "واتساب",
      cta: "ابدأ مشروعك",
    },
    hero: {
      badgeStudio: "استوديو رقمي",
      headlineLine1: "حلول رقمية ذكية",
      headlineLine2Gold: "لأصحاب",
      headlineLine2Rest: "الأعمال",
      headlineLine3: "المحلية.",
      subheadline:
        "تساعد سلامة سوليوشنز المطاعم والمقاهي والعيادات والشركات المحلية على النمو من خلال مواقع إلكترونية حديثة، أنظمة أتمتة، أنظمة حجز وطلب، وأدوات ذكية لإدارة الأعمال.",
      ctaDemos: "شاهد النماذج",
      ctaContact: "تواصل معنا",
      statProjects: "مشروع منجز",
      statNiches: "مجالات أساسية",
      statRetention: "نسبة رضا العملاء",
      statDelivery: "متوسط مدة التسليم",
    },
    services: {
      eyebrow: "ماذا نقدّم",
      title: "ثماني خدمات. استوديو واحد.",
      sub: "نصمم ونطوّر الأنظمة الرقمية التي تُدير أعمالك المحلية بهدوء، من صفحة تعريفية بسيطة إلى منظومة تشغيل كاملة مدعومة بالذكاء الاصطناعي.",
      items: [
        {
          title: "مواقع إلكترونية للأعمال",
          short: "مواقع إلكترونية فاخرة وسريعة ومهيأة لمحركات البحث، مصممة للتحويل.",
          bullets: [
            "نظام تصميم مخصص",
            "متوافق مع الجوال والتابلت وسطح المكتب",
            "أداء فائق السرعة",
            "أساسيات تحسين محركات البحث",
          ],
        },
        {
          title: "أنظمة الطلب الإلكتروني",
          short: "استقبل الطلبات أونلاين دون عمولات ضخمة.",
          bullets: [
            "قائمة طعام وإضافات",
            "استلام أو توصيل",
            "توجيه الطلبات عبر واتساب",
            "لوحة تحكم للطلبات",
          ],
        },
        {
          title: "الحجز والمواعيد",
          short: "دع عملاءك يحجزون خلال ثوانٍ، على مدار الساعة.",
          bullets: [
            "إتاحة ذكية للمواعيد",
            "تأكيد تلقائي",
            "تذكيرات وتنبيهات المواعيد الفائتة",
            "مزامنة مع التقويم",
          ],
        },
        {
          title: "أتمتة واتساب",
          short: "رد تلقائي، وتوجيه العملاء المحتملين، وتأكيد الحجوزات عبر واتساب.",
          bullets: [
            "رد تلقائي ذكي",
            "تأكيد الطلبات والحجوزات",
            "توجيه العملاء المحتملين",
            "حملات وإعادة تفاعل",
          ],
        },
        {
          title: "وكلاء دعم عملاء بالذكاء الاصطناعي",
          short: "وكلاء ذكاء اصطناعي يعملون على مدار الساعة، مدرّبون على أعمالك.",
          bullets: [
            "مدرّب على بيانات عملك",
            "قابل للنشر على الموقع وواتساب",
            "تحويل للموظف البشري عند الحاجة",
            "تحليلات المحادثات",
          ],
        },
        {
          title: "لوحات تحكم للأعمال",
          short: "راقب أعمالك بنظرة واحدة، في الوقت الفعلي.",
          bullets: [
            "مؤشرات أداء لحظية",
            "صلاحيات حسب الدور",
            "تصدير وتقارير",
            "متوافقة مع الجوال",
          ],
        },
        {
          title: "المنزل والمكتب الذكي",
          short: "تحكم صوتي، وحساسات، وأتمتة تعمل ببساطة.",
          bullets: [
            "الإضاءة والتحكم بالمناخ",
            "التحكم بالدخول",
            "المساعدات الصوتية",
            "إجراءات مخصصة",
          ],
        },
        {
          title: "حلول تقنية مخصصة",
          short: "أنظمة مصممة خصيصًا لمشاكل الأعمال المعقدة.",
          bullets: [
            "أدوات داخلية",
            "تكاملات وواجهات برمجية",
            "أتمتة سير العمل",
            "استشارات تقنية",
          ],
        },
      ],
    },
    servicesPage: {
      eyebrow: "خدماتنا",
      heading1: "قدرات الاستوديو،",
      headingGold: "مصممة لنمو",
      headingRest: "أعمالك المحلية.",
      sub: "ثماني خدمات مركّزة. يمكننا تقديم أي منها بشكل مستقل، أو دمجها في نظام تشغيل رقمي متكامل لعملك.",
      marquee: ["تصميم", "هندسة", "أتمتة", "ذكاء اصطناعي", "استراتيجية"],
      ctaTitle: "لست متأكدًا من أين تبدأ؟",
      ctaSub: "أرسل لنا اسم عملك ومشكلة واحدة تتمنى لو تُحل من تلقاء نفسها. سنرد عليك بخطة واضحة.",
      ctaButton: "تواصل معنا",
    },
    whoWeHelp: {
      eyebrow: "من نخدم",
      title: "مبني لأجل الأعمال التي يحبها حيّك.",
      sub: "نركّز بعمق على ثلاثة مجالات حتى تكون الأنظمة التي نبنيها دقيقة، لا عامة.",
      liveDemo: "نموذج حي",
      niches: [
        {
          name: "المطاعم",
          blurb: "طلب إلكتروني، حجوزات، إدارة قوائم طعام، وأتمتة واتساب تملأ المزيد من الطاولات.",
        },
        {
          name: "المقاهي",
          blurb: "برامج ولاء، طلب عبر الجوال، ومواقع أنيقة تحوّل الزوار الجدد إلى عملاء دائمين.",
        },
        {
          name: "العيادات",
          blurb: "حجز المواعيد، تذكيرات، استقبال المرضى، ومواقع نظيفة وموثوقة تبني الثقة.",
        },
      ],
    },
    whyUs: {
      eyebrow: "لماذا سلامة سوليوشنز",
      title: "حرفية عالية. نتائج حقيقية.",
      sub: "نجمع بين ذوق تصميمي راقٍ وهندسة جادة — ولا نُطلق إلا عملاً نضع اسمنا عليه، لأننا فعلاً نفعل.",
      items: [
        {
          title: "مبني لأجل الأعمال المحلية",
          body: "نفهم طبيعة الأعمال الصغيرة. بلا برمجيات مبالغ فيها، وبلا مصطلحات معقدة — فقط أنظمة تُغطي تكلفتها بنفسها.",
        },
        {
          title: "تصميم فاخر، هندسة حقيقية",
          body: "تصاميم تبدو كعلامة تجارية تثق بها، بأكواد لا تتعطل بعد شهرين.",
        },
        {
          title: "ذكاء اصطناعي حين يستحق",
          body: "نستخدم الذكاء الاصطناعي عندما يوفر وقتًا أو مالًا فعلاً — لا كملصق تسويقي على الصفحة الرئيسية.",
        },
        {
          title: "تسليم سريع، أسعار واضحة",
          body: "معظم المشاريع تُسلَّم خلال أيام لا أشهر. تعرف النطاق والسعر والمدة الزمنية مسبقًا.",
        },
      ],
    },
    about: {
      eyebrow: "من خلف هذا العمل",
      title: "استوديو له اسم، لا مجرد شعار.",
      sub: "أنت لا تتعامل مع وكالة مجهولة. أنت تعمل مباشرة مع الشخص الذي يبني ويقف خلف كل مشروع.",
      founderName: "أحمد سلامة",
      founderRole: "مؤسس، سلامة سوليوشنز",
      basedIn: "مقيم في رام الله، فلسطين",
      bioParagraphs: [
        "أحمد سلامة هو مؤسس سلامة سوليوشنز، ويعمل من رام الله. تعلّم بناء البرمجيات بنفسه قبل أن يدرسها أكاديميًا بوقت طويل، وما زال هذان الجانبان يشكّلان طريقة عمله اليوم: حدس من تعلّم عبر بناء أشياء حقيقية، إلى جانب انضباط من عاد ليدرس الأساسيات بشكل صحيح.",
        "أسّس هذا الاستوديو لسبب بسيط. كل صاحب مطعم أو مقهى أو عيادة تحدّث معه شعر أن التكنولوجيا شيء مصمم لغيره، ثم يُسلَّم له كقالب جاهز. أراد أحمد العكس تمامًا. أراد لكل صاحب عمل يتعامل معه أن يفتح موقعه أو نظامه الجديد ويشعر أنه صُنع فعلاً من أجل مجتمعه، وزبائنه، واسمه المعلّق على الباب.",
        "وهكذا لا يزال يعمل حتى اليوم. يرد على واتساب بنفسه، ويقرأ كل رسالة تصل عبر نموذج التواصل، ويبني كل مشروع بنفسه دون تمريره إلى فريق لن تقابله أبدًا. إذا تواصلت مع سلامة سوليوشنز، فأنت تتحدث مباشرة مع الشخص الذي سيبني مشروعك.",
      ],
      studioLine: "سلامة سوليوشنز استوديو رقمي فلسطيني يبني مواقع إلكترونية، وأنظمة طلب، وأدوات حجز، وأتمتة للمطاعم والمقاهي والعيادات في المنطقة — عمل مصمم ومنفَّذ محليًا، من قِبل شخص يفهم السوق الذي يُبنى من أجله.",
    },
    aboutPage: {
      eyebrow: "من نحن",
      heading1: "تعرّف على",
      headingGold: "الاستوديو الذي يبني مشروعك.",
      sub: "بلا مدراء حسابات، وبلا تسليم بين فرق — فقط الشخص الذي يصمم ويبني ويُطلق مشروعك من البداية للنهاية.",
    },
    demoCards: {
      eyebrow: "مواقع تجريبية",
      title: "أربعة نماذج مميزة. أربع أجواء مختلفة.",
      sub: "لكل نموذج رابطه الخاص، وهويته، وألوانه، ومزاياه — تمامًا كما نُسلّمه لعميل حقيقي.",
      viewDemo: "شاهد النموذج الحي",
    },
    demosList: {
      restaurant: { type: "مطعم", description: "مطبخ متوسطي بالحطب مع طلب إلكتروني وحجوزات." },
      palo: { type: "مقهى", description: "نظام مقهى متكامل يشمل الطلب، برنامج الولاء، لوحة تحكم إدارية، مهام الباريستا، وتحليلات لحظية." },
      cafe: { type: "مقهى", description: "مقهى مختص ببرنامج ولاء، طلب إلكتروني، وقصة علامة تجارية دافئة." },
      clinic: { type: "عيادة", description: "موقع رعاية أولية موثوق مع حجز مواعيد إلكتروني." },
    },
    demosPage: {
      eyebrow: "معرض النماذج",
      heading1: "أربعة نماذج مميزة.",
      headingGold: "أربع أجواء مختلفة.",
      sub: "لكل نموذج رابطه الخاص، وهويته، ومزاياه — تمامًا كما نُسلّمه لعميل حقيقي.",
    },
    pricing: {
      eyebrow: "خطط الأسعار",
      title: "نطاق واضح. أسعار صريحة.",
      sub: "ثلاث نقاط انطلاق. كل خطة تُصمَّم حسب عملك — ما تراه هو الحد الأدنى، لا سقفًا مخفيًا.",
      startingFrom: "ابتداءً من",
      mostPopular: "الأكثر طلبًا",
      deliveryLabel: "مدة التسليم",
      startWith: (name) => `ابدأ بـ ${name}`,
      paymentMethods: "طرق الدفع المقبولة: تحويل بنكي ونقدًا",
      plans: {
        basic: {
          name: "أساسية",
          tagline: "احصل على حضور إلكتروني احترافي — بسرعة.",
          audience: "للأعمال الصغيرة التي تحتاج موقعًا نظيفًا وموثوقًا.",
          delivery: "5–7 أيام",
          features: [
            "موقع بصفحة واحدة",
            "تصميم متجاوب مع الجوال",
            "زر تواصل / واتساب",
            "إعداد أساسي لتحسين محركات البحث",
            "إرشادات إعداد الاستضافة",
          ],
        },
        standard: {
          name: "قياسية",
          tagline: "موقع أعمال حقيقي يتفاعل مع العملاء.",
          audience: "للأعمال التي تحتاج قوائم طعام أو خدمات أو نماذج حجز.",
          delivery: "7–14 يومًا",
          features: [
            "موقع متعدد الأقسام",
            "قائمة طعام أو خدمات أو نموذج حجز",
            "أتمتة واتساب",
            "لوحة تحكم أو إدارة نماذج أساسية",
            "جولة تعديل واحدة",
            "دعم إعداد الدومين والبريد الإلكتروني",
          ],
        },
        premium: {
          name: "مميزة",
          tagline: "نظام ذكي متكامل من الألف إلى الياء.",
          audience: "للأعمال التي تريد تفوقًا رقميًا حقيقيًا.",
          delivery: "2–4 أسابيع",
          features: [
            "موقع إلكتروني مخصص بالكامل",
            "نظام حجز أو طلب",
            "روبوت محادثة أو وكيل دعم بالذكاء الاصطناعي",
            "عرض للوحة تحكم إدارية",
            "سير عمل آلي",
            "دعم ذو أولوية",
          ],
        },
      },
      note: "قد تتغير الأسعار حسب احتياجات العمل وتعقيد النظام. نرسل عرض سعر واضح قبل بدء أي عمل.",
    },
    pricingPage: {
      eyebrow: "الأسعار",
      heading1: "خطط بسيطة.",
      headingGold: "أنظمة حقيقية.",
      sub: "اختر نقطة الانطلاق المناسبة لعملك. سنرسل عرض سعر مخصصًا يوضح النطاق النهائي قبل بدء أي عمل.",
    },
    notFound: {
      badge: "الصفحة غير موجودة",
      heading: "لا شيء هنا.",
      body: "الصفحة التي حاولت فتحها غير موجودة. عد إلى الرئيسية وتابع استكشاف الاستوديو.",
      backHome: "العودة للرئيسية",
      viewDemos: "شاهد النماذج",
    },
    marquee: [
      "مواقع إلكترونية",
      "طلب إلكتروني",
      "أنظمة حجز",
      "أتمتة واتساب",
      "وكلاء ذكاء اصطناعي",
      "لوحات تحكم",
      "مساحات ذكية",
      "حلول تقنية مخصصة",
    ],
    contact: {
      eyebrow: "تواصل معنا",
      title: "أخبرنا عمّا تريد بناءه.",
      sub: "أرسل رسالة سريعة، ونرد خلال يوم عمل واحد بالنطاق والسعر والمدة الزمنية — دون أي ضغط للبيع.",
      emailLabel: "البريد الإلكتروني",
      whatsappLabel: "واتساب",
      respondNote:
        "نرد من الإثنين إلى السبت. للأسئلة السريعة، واتساب هو الأسرع.",
      fieldName: "الاسم",
      fieldEmail: "البريد الإلكتروني",
      fieldBusiness: "نوع النشاط التجاري",
      fieldService: "الخدمة المطلوبة",
      fieldMessage: "الرسالة",
      placeholderName: "مثال: سارة أحمد",
      placeholderEmail: "sara@example.com",
      placeholderMessage:
        "أخبرنا عمّا تريد بناءه، وما جربته سابقًا، وكيف يبدو النجاح بالنسبة لك.",
      errorName: "الرجاء إضافة اسمك.",
      errorEmail: "الرجاء إضافة بريدك الإلكتروني.",
      errorMessage: "أضف وصفًا سريعًا، ولو سطر واحد يكفي.",
      sending: "جارٍ الإرسال...",
      send: "إرسال الرسالة",
      chatWhatsapp: "تحدث عبر واتساب",
      privacyNote: "لا نشارك بياناتك مع أي جهة. أبدًا.",
      privacyLink: "سياسة الخصوصية",
      successTitle: "تم استلام رسالتك.",
      successBody:
        "سنرد عليك خلال يوم عمل واحد. للمحادثة الفورية، تواصل معنا عبر واتساب.",
      thereFallback: "هناك",
      sendError:
        "تعذّر إرسال الرسالة. حاول مرة أخرى أو تواصل معنا عبر واتساب.",
    },
    contactPage: {
      heading1: "لنبنِ معًا شيئًا",
      headingGold: "يتذكره الناس.",
    },
    businessTypes: [
      "مطعم",
      "مقهى",
      "عيادة",
      "متجر تجزئة",
      "صالون / سبا",
      "فندق / نزل",
      "عقارات",
      "أخرى",
    ],
    serviceOptions: [
      "موقع إلكتروني للأعمال",
      "نظام طلب أونلاين",
      "نظام حجز ومواعيد",
      "أتمتة واتساب",
      "وكيل دعم عملاء بالذكاء الاصطناعي",
      "لوحة تحكم للأعمال",
      "منزل / مكتب ذكي",
      "حل تقني مخصص",
      "لست متأكدًا بعد — أرشدوني",
    ],
    footer: {
      studio: "الاستوديو",
      demos: "نماذج",
      rights: "جميع الحقوق محفوظة.",
      builtNote: "نُصمم ونُطوّر داخليًا بعناية",
      privacyPolicy: "سياسة الخصوصية",
      credibility: "سلامة سوليوشنز · تعمل منذ عام 2024",
      tagline: "أنظمة رقمية ذكية، مواقع إلكترونية، أتمتة، أنظمة حجز وطلب، وأدوات ذكاء اصطناعي للأعمال المحلية.",
    },
    privacyPage: {
      eyebrow: "قانوني",
      titleMain: "سياسة",
      titleGold: "الخصوصية",
      lastUpdated: "آخر تحديث: 28 يوليو 2026",
      shortVersion:
        "باختصار: تبقى معلوماتك معنا، بأمان تام. نستخدمها فقط للتواصل معك بخصوص مشروعك — لا تُباع أبدًا، ولا تُشارك أبدًا، ولا تُسلَّم لأي جهة أخرى أبدًا.",
      sections: [
        {
          title: "1. من نحن",
          body: "سلامة سوليوشنز استوديو رقمي يبني مواقع إلكترونية، وأنظمة طلب، وأدوات حجز، وأتمتة للأعمال المحلية. توضح هذه الصفحة، بلغة بسيطة، ما الذي يحدث لمعلوماتك عند تواصلك معنا.",
        },
        {
          title: "2. ما الذي نجمعه",
          body: "عند استخدامك نموذج التواصل أو مراسلتنا عبر البريد الإلكتروني/واتساب، نجمع فقط ما تشاركه معنا مباشرة: اسمك، بريدك الإلكتروني، رقم هاتفك/واتساب، نوع نشاطك التجاري، وتفاصيل رسالتك. لا نجمع معلومات الدفع عبر هذا الموقع.",
        },
        {
          title: "3. كيف نستخدمها",
          body: "نستخدم معلوماتك لسبب واحد فقط: الرد عليك، وإعداد عرض السعر الخاص بك، وتنفيذ المشروع الذي توظفنا من أجله. هذا كل شيء — لا نبيعها، ولا نؤجرها، ولا نسلّمها لأي جهة أخرى، ولا نستخدمها للتسويق الذي لم تطلبه.",
        },
        {
          title: "4. الحفاظ عليها آمنة",
          body: "تبقى بياناتك معنا، بأمان تام. رسائل نموذج التواصل تمر عبر خدمة بريد إلكتروني مشفّرة قياسية لتصل إلى صندوق واردنا، وتبقى رسائل واتساب داخل منصة واتساب الآمنة الخاصة بها. لا نستخدم أدوات تتبع أو إعلانات على هذا الموقع.",
        },
        {
          title: "5. حقوقك",
          body: "يمكنك أن تطلب منا في أي وقت أن نخبرك بالمعلومات التي نحتفظ بها عنك، أو تصحيحها، أو حذفها. تواصل معنا عبر التفاصيل أدناه وسنهتم بذلك بسرعة.",
        },
        {
          title: "6. التغييرات على هذه السياسة",
          body: "قد نحدّث هذه الصفحة من حين لآخر مع نمو العمل. ستكون أحدث نسخة متاحة دائمًا هنا.",
        },
      ],
      contactSectionTitle: "7. تواصل معنا",
      contactSectionBody: (email, whatsapp) =>
        `أسئلة حول هذه السياسة أو بياناتك؟ راسلنا عبر البريد الإلكتروني ${email} أو عبر واتساب على ${whatsapp}.`,
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return window.localStorage.getItem(STORAGE_KEY) === "ar" ? "ar" : "en";
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      dir: translations[lang].dir,
      isAr: lang === "ar",
      t: translations[lang],
      setLang,
      toggleLanguage: () => setLang((l) => (l === "en" ? "ar" : "en")),
    }),
    [lang]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
